import {
  DATABASE_LARGE_DATA_POLICY,
  type DatabaseShardManifestEntry,
  planDatabaseRowShardMerges,
} from '@streamlain/database-identity';

import type { DatabaseRowEdit } from './database-row-mutation-contract';

/** One small shard joining its order-neighbour, as row edits of a move. */
export interface DatabaseRowShardMergeEdits {
  readonly source: DatabaseShardManifestEntry;
  readonly target: DatabaseShardManifestEntry;
  /** The source drops every row; the writer then removes it from the manifest. */
  readonly sourceEdits: readonly DatabaseRowEdit[];
  /** The target takes them in the source's order, before or after its own. */
  readonly targetEdits: readonly DatabaseRowEdit[];
}

/**
 * The merges a write leaves due (planDatabaseRowShardMerges) as edits of a
 * move: every row of the source transfers to the neighbour. A shard the write
 * emptied needs none, the writer removes it itself. A merge whose source rows
 * `rowIdsOf` cannot name stays for a later write, which plans it again.
 */
export function planDatabaseRowShardMergeEdits(
  shards: readonly DatabaseShardManifestEntry[],
  changedShardIds: ReadonlySet<string>,
  targetBytes: number,
  rowIdsOf: (
    source: DatabaseShardManifestEntry,
    target: DatabaseShardManifestEntry
  ) => readonly string[] | undefined
): DatabaseRowShardMergeEdits[] {
  const byId = new Map(shards.map(shard => [shard.shardId, shard]));
  return planDatabaseRowShardMerges(
    shards,
    changedShardIds,
    DATABASE_LARGE_DATA_POLICY.shard.mergeBelowBytes,
    targetBytes
  ).flatMap(merge => {
    const source = byId.get(merge.sourceShardId);
    const target = merge.targetShardId && byId.get(merge.targetShardId);
    if (!source || !target) return [];
    const rowIds = rowIdsOf(source, target);
    if (!rowIds) return [];
    const offset = merge.position === 'prepend' ? 0 : target.rowCount;
    return [
      {
        source,
        target,
        sourceEdits: [{ type: 'delete', rowIds: [...rowIds] }],
        targetEdits: rowIds.map(
          (rowId, index): DatabaseRowEdit => ({
            type: 'transfer',
            fromDocId: source.serverDocId,
            rowId,
            index: offset + index,
          })
        ),
      },
    ];
  });
}
